import Button from 'ext-ui/components/Button/Button'
import ButtonGroup from 'ext-ui/components/ButtonGroup/ButtonGroup'
import Modal from 'ext-ui/components/Modal/Modal'
import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import style from './PhysicalCardIssuanceModal.style'

/**
 * Which of the two governed entry points opened this modal.
 *
 * The split is a ROLE split, not a preference: an Admin/Owner orders a physical
 * card directly, and everyone else raises a request that routes to an
 * Admin/Owner (the Card Approver). The caller decides which it may offer; this
 * modal only reflects the choice back.
 */
export type PhysicalCardIssuanceIntent = 'issue' | 'request'

type PhysicalCardIssuanceModalProps = {
  /** The open intent, or undefined when the modal is closed. */
  intent: PhysicalCardIssuanceIntent | undefined
  onClose: () => void
}

/**
 * Placeholder for the physical-card issue/request flows (EX-40738).
 *
 * DELIBERATELY INERT. The ticket adds the two Create New menu entries ahead of
 * the flows themselves: there is no issuance or request API yet — the backend's
 * `ISSUE_PHYSICAL_CARD` / `REQUEST_PHYSICAL_CARD_ISSUE` actions exist in the
 * enum but are not in the served capability catalog — so there is nothing to
 * submit to.
 *
 * It therefore renders NO form. A cardholder/limit/shipping form with a dead
 * submit would look like a working flow and collect data nothing receives; the
 * design prototype's form is a visual reference for the flow that lands later,
 * not something to reproduce while the backend is absent. Saying plainly that
 * the flow is coming keeps the gap visible rather than papered over.
 */
const PhysicalCardIssuanceModal: React.FC<PhysicalCardIssuanceModalProps> = ({
  intent,
  onClose,
}): ReactElement => {
  const translate = useTranslate()

  const isIssue = intent === 'issue'

  const title = isIssue
    ? translate('Issue a physical card')
    : translate('Request a physical card')

  const description = isIssue
    ? translate('Order a new physical card directly.')
    : translate('A governed request that routes to an Admin or Owner.')

  const body = isIssue
    ? translate(
        "Ordering a physical card from here isn't available yet. Your Extend account team can order one for you in the meantime.",
      )
    : translate(
        "Requesting a physical card from here isn't available yet. Ask an Admin or Owner in your organization to order one in the meantime.",
      )

  return (
    <Modal
      open={intent !== undefined}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <ButtonGroup>
          <Button
            variant="primary"
            onClick={onClose}
            data-testid="physical-card-issuance-close"
          >
            {translate('Close')}
          </Button>
        </ButtonGroup>
      }
    >
      <div css={style} data-testid="physical-card-issuance-modal">
        <p>{body}</p>
      </div>
    </Modal>
  )
}

export default PhysicalCardIssuanceModal
